import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../api';
import { isChairmanReadOnly } from '../utils/access';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/members';

function Members() {
  const [members, setMembers]           = useState([]);
  const [showForm, setShowForm]         = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [editData, setEditData]         = useState({});
  const [auditMember, setAuditMember]   = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [showAudit, setShowAudit]       = useState(false);
  const [sigMember, setSigMember]       = useState(null);
  const [showSigModal, setShowSigModal] = useState(false);
  const [sigPreview, setSigPreview]     = useState(null);
  const [sigUploading, setSigUploading] = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const fileInputRef                    = useRef(null);
  const readOnly = isChairmanReadOnly();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [form, setForm] = useState({
    full_name: '', member_number: '', phone: '', email: '',
    district: '', date_joined: '', status: 'active',
    national_id: '', location: ''
  });

  useEffect(() => { fetchMembers(); }, []);

  const fetchMembers = () => {
    axios.get(API, { headers })
      .then(res => setMembers(res.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load members'));
  };

  const openAddForm = async () => {
    try {
      const res = await axios.get(`${API}/next-number`, { headers });
      setForm({
        full_name: '', member_number: res.data.next_number, phone: '', email: '',
        district: '', date_joined: new Date().toISOString().split('T')[0],
        status: 'active', national_id: '', location: ''
      });
    } catch {
      setForm({
        full_name: '', member_number: '', phone: '', email: '',
        district: '', date_joined: new Date().toISOString().split('T')[0],
        status: 'active', national_id: '', location: ''
      });
    }
    setShowForm(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await axios.post(API, form, { headers });
      setSuccess('Member added successfully');
      setShowForm(false);
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
    }
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setEditData({
      full_name:     member.full_name     || '',
      member_number: member.member_number || '',
      phone:         member.phone         || '',
      email:         member.email         || '',
      district:      member.district      || '',
      date_joined:   member.date_joined ? member.date_joined.slice(0, 10) : '',
      status:        member.status        || 'active',
      national_id:   member.national_id   || '',
      location:      member.location      || ''
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  const saveEdit = async (id) => {
    setError(''); setSuccess('');
    try {
      await axios.patch(`${API}/${id}`, editData, { headers });
      setSuccess('Member updated successfully');
      setEditingId(null);
      setEditData({});
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update member');
    }
  };

  const viewAudit = async (member) => {
    setAuditMember(member);
    try {
      const res = await axios.get(`${API}/${member.id}/audit`, { headers });
      setAuditHistory(res.data);
      setShowAudit(true);
    } catch {
      setError('Failed to load audit history');
    }
  };

  // ── SIGNATURE UPLOAD ──────────────────────────────────────
  const openSigModal = (member) => {
    setSigMember(member);
    setSigPreview(member.signature_image || null);
    setShowSigModal(true);
  };

  const handleSigFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width  = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        // Remove near-white background (threshold R,G,B > 140)
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] > 140 && data[i+1] > 140 && data[i+2] > 140) {
            data[i+3] = 0;
          }
        }
        ctx.putImageData(imageData, 0, 0);
        setSigPreview(canvas.toDataURL('image/png'));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const saveSig = async () => {
    if (!sigPreview || !sigMember) return;
    setSigUploading(true);
    try {
      await axios.patch(`${API}/${sigMember.id}/signature`, { signature_image: sigPreview }, { headers });
      setSuccess(`Signature saved for ${sigMember.full_name}`);
      setShowSigModal(false);
      setSigMember(null);
      setSigPreview(null);
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save signature');
    }
    setSigUploading(false);
  };

  const removeSig = async () => {
    if (!sigMember) return;
    if (!window.confirm('Remove signature for ' + sigMember.full_name + '?')) return;
    try {
      await axios.patch(`${API}/${sigMember.id}/signature`, { signature_image: null }, { headers });
      setSuccess('Signature removed');
      setShowSigModal(false);
      setSigMember(null);
      setSigPreview(null);
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove signature');
    }
  };

  const districts = ['KAMPALA', 'MUBENDE', 'JINJA', 'KANUNGU', 'KABALE', 'KISORO', 'RUKUNGIRI', 'OTHER'];

  return (
    <div style={{ padding: '20px' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#2c3e50', margin: 0 }}>Members</h2>
        {!readOnly && (
        <button onClick={showForm ? () => setShowForm(false) : openAddForm} style={btnStyle}>
          {showForm ? 'Cancel' : '+ Add Member'}
        </button>
        )}
      </div>

      {error   && <div style={alertStyle('#e74c3c')}>{error}   <span onClick={() => setError('')}   style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {success && <div style={alertStyle('#2ecc71')}>{success} <span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {!readOnly && showForm && (
        <form onSubmit={handleAdd} style={formStyle}>
          <h3 style={{ margin: '0 0 15px', color: '#2c3e50', width: '100%' }}>New Member</h3>
          <div style={fieldStyle}><label style={labelStyle}>Full Name *</label><input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} style={inputStyle} required /></div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Member Number *</label>
            <input value={form.member_number} onChange={e => setForm({ ...form, member_number: e.target.value })} style={{ ...inputStyle, background: '#f0f8f0', fontFamily: 'monospace', fontWeight: '700' }} required />
            <span style={{ fontSize: '11px', color: '#27ae60' }}>Auto-generated — editable if needed</span>
          </div>
          <div style={fieldStyle}><label style={labelStyle}>Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} /></div>
          <div style={fieldStyle}><label style={labelStyle}>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} /></div>
          <div style={fieldStyle}>
            <label style={labelStyle}>District</label>
            <select value={form.district} onChange={e => setForm({ ...form, district: e.target.value })} style={inputStyle}>
              <option value="">-- Select --</option>
              {districts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div style={fieldStyle}><label style={labelStyle}>Join Date</label><input type="date" value={form.date_joined} onChange={e => setForm({ ...form, date_joined: e.target.value })} style={inputStyle} /></div>
          <div style={fieldStyle}><label style={labelStyle}>National ID</label><input value={form.national_id} onChange={e => setForm({ ...form, national_id: e.target.value })} style={inputStyle} /></div>
          <div style={fieldStyle}><label style={labelStyle}>Location</label><input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} style={inputStyle} placeholder="Village/Parish" /></div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Status</label>
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="expelled">Expelled</option>
              <option value="resigned">Resigned</option>
            </select>
          </div>
          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type="submit" style={btnStyle}>Save Member</button>
            <button type="button" onClick={() => setShowForm(false)} style={{ ...btnStyle, background: '#95a5a6' }}>Cancel</button>
          </div>
        </form>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#2c3e50', color: 'white' }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Full Name</th>
              <th style={thStyle}>No.</th>
              <th style={thStyle}>Phone</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>District</th>
              <th style={thStyle}>Join Date</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Signature</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan="10" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No members yet</td></tr>
            )}
            {members.map((m, i) => (
              editingId === m.id ? (
                <tr key={m.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={tdStyle}><input value={editData.full_name} onChange={e => setEditData({ ...editData, full_name: e.target.value })} style={inlineInput} /></td>
                  <td style={tdStyle}><input value={editData.member_number} onChange={e => setEditData({ ...editData, member_number: e.target.value })} style={inlineInput} /></td>
                  <td style={tdStyle}><input value={editData.phone} onChange={e => setEditData({ ...editData, phone: e.target.value })} style={inlineInput} /></td>
                  <td style={tdStyle}><input value={editData.email} onChange={e => setEditData({ ...editData, email: e.target.value })} style={inlineInput} /></td>
                  <td style={tdStyle}>
                    <select value={editData.district} onChange={e => setEditData({ ...editData, district: e.target.value })} style={inlineInput}>
                      {districts.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </td>
                  <td style={tdStyle}><input type="date" value={editData.date_joined} onChange={e => setEditData({ ...editData, date_joined: e.target.value })} style={inlineInput} /></td>
                  <td style={tdStyle}>
                    <select value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value })} style={inlineInput}>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="expelled">Expelled</option>
                      <option value="resigned">Resigned</option>
                    </select>
                  </td>
                  <td style={tdStyle}>—</td>
                  <td style={tdStyle}>
                    {!readOnly && <button onClick={() => saveEdit(m.id)} style={{ ...smallBtn, background: '#2ecc71', marginRight: '4px' }}>Save</button>}
                    <button onClick={cancelEdit} style={{ ...smallBtn, background: '#95a5a6' }}>Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={m.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={tdStyle}><strong>{m.full_name}</strong></td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: '700' }}>{m.member_number}</td>
                  <td style={tdStyle}>{m.phone || '—'}</td>
                  <td style={tdStyle}>{m.email || '—'}</td>
                  <td style={tdStyle}>{m.district || '—'}</td>
                  <td style={tdStyle}>{m.date_joined ? m.date_joined.slice(0, 10) : '—'}</td>
                  <td style={tdStyle}>
                    <span style={{
                      background: m.status === 'active' ? '#2ecc71' : m.status === 'suspended' ? '#f39c12' : '#e74c3c',
                      color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'
                    }}>
                      {m.status}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {m.signature_image ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <img src={m.signature_image} alt="sig" style={{ height: '28px', maxWidth: '70px', objectFit: 'contain', background: '#f9f9f9', border: '1px solid #ddd', borderRadius: '3px', padding: '2px' }} />
                        {!readOnly && <button onClick={() => openSigModal(m)} style={{ ...smallBtn, background: '#e67e22', fontSize: '10px', padding: '2px 6px' }}>✎</button>}
                      </div>
                    ) : (
                      !readOnly && <button onClick={() => openSigModal(m)} style={{ ...smallBtn, background: '#3498db', fontSize: '11px' }}>+ Signature</button>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {!readOnly && <button onClick={() => startEdit(m)} style={{ ...smallBtn, marginRight: '4px' }}>Edit</button>}
                    <button onClick={() => viewAudit(m)} style={{ ...smallBtn, background: '#9b59b6' }}>History</button>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>

      {/* ── SIGNATURE UPLOAD MODAL ── */}
      {showSigModal && sigMember && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, maxWidth: '460px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>✍ Member Signature — {sigMember.full_name}</h3>
              <button onClick={() => { setShowSigModal(false); setSigPreview(null); }} style={{ ...smallBtn, background: '#e74c3c' }}>✕</button>
            </div>
            <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 16px' }}>
              Upload a scan or photo of the member's signature. Near-white/grey background will be removed automatically. This signature will be printed on Loan Agreements and the MBA.
            </p>

            {sigPreview ? (
              <div style={{ background: '#f9f9f9', border: '1px solid #ddd', borderRadius: '6px', padding: '16px', textAlign: 'center', marginBottom: '16px' }}>
                <img src={sigPreview} alt="Signature preview" style={{ maxHeight: '80px', maxWidth: '300px', objectFit: 'contain' }} />
                <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '8px' }}>Preview — background removed</div>
              </div>
            ) : (
              <div style={{ background: '#f9f9f9', border: '2px dashed #ddd', borderRadius: '6px', padding: '32px', textAlign: 'center', marginBottom: '16px', color: '#aaa' }}>
                No signature uploaded
              </div>
            )}

            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleSigFile} style={{ display: 'none' }} />

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => fileInputRef.current?.click()} style={{ ...btnStyle, background: '#3498db' }}>
                📁 {sigPreview ? 'Replace Image' : 'Upload Image'}
              </button>
              {!readOnly && sigPreview && (
                <button onClick={saveSig} disabled={sigUploading} style={{ ...btnStyle, background: sigUploading ? '#95a5a6' : '#27ae60' }}>
                  {sigUploading ? 'Saving…' : '✓ Save Signature'}
                </button>
              )}
              {!readOnly && sigMember.signature_image && (
                <button onClick={removeSig} style={{ ...btnStyle, background: '#e74c3c' }}>
                  🗑 Remove
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── AUDIT HISTORY MODAL ── */}
      {showAudit && auditMember && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>Edit History — {auditMember.full_name}</h3>
              <button onClick={() => setShowAudit(false)} style={{ ...smallBtn, background: '#e74c3c' }}>Close</button>
            </div>
            {auditHistory.length === 0 ? (
              <p style={{ color: '#95a5a6', textAlign: 'center' }}>No edit history yet</p>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    <th style={thStyle}>Field</th>
                    <th style={thStyle}>Old Value</th>
                    <th style={thStyle}>New Value</th>
                    <th style={thStyle}>Changed By</th>
                    <th style={thStyle}>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {auditHistory.map((h, i) => (
                    <tr key={h.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}><strong>{h.field_name}</strong></td>
                      <td style={{ ...tdStyle, color: '#e74c3c' }}>{h.old_value || '—'}</td>
                      <td style={{ ...tdStyle, color: '#2ecc71' }}>{h.new_value || '—'}</td>
                      <td style={tdStyle}>{h.changed_by_name || 'system'}</td>
                      <td style={tdStyle}>{new Date(h.changed_at).toLocaleString('en-GB')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const btnStyle     = { background: '#2c3e50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' };
const smallBtn     = { background: '#2c3e50', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };
const formStyle    = { background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', border: '1px solid #ecf0f1' };
const fieldStyle   = { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '200px' };
const labelStyle   = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle   = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const inlineInput  = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #f39c12', fontSize: '13px', width: '100%', background: '#fffde7' };
const tableStyle   = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle      = { padding: '10px', textAlign: 'left', fontSize: '13px' };
const tdStyle      = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };
const alertStyle   = (color) => ({ background: color, color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' });
const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const modalBox     = { background: 'white', borderRadius: '8px', padding: '24px', width: '90%', maxWidth: '900px', maxHeight: '80vh', overflowY: 'auto' };

export default Members;